import {Component, Inject} from '@angular/core';
import {CommonModule} from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {TuiDialogContext, TuiAlertService} from '@taiga-ui/core';
import {POLYMORPHEUS_CONTEXT} from '@taiga-ui/polymorpheus';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {GateweeIconComponent} from '../../gatewee-icon/gatewee-icon.component';
import {GateweeButtonComponent} from '../../gatewee-button/gatewee-button.component';
import {Observable} from 'rxjs';
import {PaymentProvider} from '@gatewee/types/provider';

interface DialogData {
  id: number;
  submitCredentialsFn: (
    provider: PaymentProvider,
    payload: unknown,
  ) => Observable<void>;
  provider: PaymentProvider;
}

@Component({
  selector: 'app-cdc-credentials',
  templateUrl: './cdc-credentials.component.html',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    GateweeIconComponent,
    GateweeButtonComponent,
  ],
})
export class CdcCredentialsComponent {
  form: FormGroup;
  submitting = false;
  showSecret = false;
  id!: number;
  submitCredentialsFn!: (
    provider: PaymentProvider,
    payload: unknown,
  ) => Observable<void>;
  provider!: PaymentProvider;

  fileNames: {[k in 'mtlsCert' | 'mtlsKey']?: string} = {};

  constructor(
    private fb: FormBuilder,
    private alerts: TuiAlertService,
    private translate: TranslateService,
    @Inject(POLYMORPHEUS_CONTEXT)
    private readonly context: TuiDialogContext<boolean, DialogData>,
  ) {
    this.id = this.context.data.id;
    this.submitCredentialsFn = this.context.data.submitCredentialsFn;
    this.provider = this.context.data.provider;
    this.form = this.fb.group({
      sandbox: [false],
      in: this.fb.group({
        pixKey: ['', [Validators.required, Validators.minLength(3)]],
        // strings
        clientKey: ['', [Validators.required, Validators.minLength(3)]],
        clientSecret: ['', [Validators.required, Validators.minLength(3)]],

        // arquivos -> base64 do conteúdo
        mtlsCert: [null, [Validators.required]],
        mtlsKey: [null, [Validators.required]],
      }),
      out: this.fb.group({
        // strings
        clientKey: ['', [Validators.required, Validators.minLength(3)]],
        clientSecret: ['', [Validators.required, Validators.minLength(3)]],
      }),
    });
  }

  async onFileChange(event: Event, controlName: 'mtlsCert' | 'mtlsKey') {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      this.form.get('in')?.get(controlName)?.setValue(null);
      this.fileNames[controlName] = '';
      return;
    }

    try {
      const base64 = await this.fileToBase64(file);
      // base64 puro, sem prefixo data:...
      const clean = base64.split(',').pop() ?? base64;
      this.form.get('in')?.get(controlName)?.setValue(clean);
      this.form.get('in')?.get(controlName)?.markAsDirty();
      this.fileNames[controlName] = file.name;
    } catch (e) {
      console.error(
        this.translate.instant(
          'features.integrations.credentials.fileReadError',
        ),
        e,
      );
      this.form.get('in')?.get(controlName)?.setErrors({fileRead: true});
    }
  }

  private fileToBase64(file: File): Promise<string> {
    // lê binário e converte para base64
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(reader.error);
      reader.onload = () => resolve(reader.result as string);
      reader.readAsDataURL(file);
    });
  }

  async onSubmit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.submitting = true;

    const payload = this.form.value as {
      sandbox: boolean;
      in: {
        pixKey: string;
        clientKey: string;
        clientSecret: string;
        mtlsCert: string;
        mtlsKey: string;
      };
      out: {
        clientKey: string;
        clientSecret: string;
      };
    };

    let handledSuccess = false;
    const handleSuccess = () => {
      if (handledSuccess) return;
      handledSuccess = true;
      this.alerts
        .open(
          this.translate.instant(
            'features.integrations.credentials.success.message',
          ),
          {
            label: this.translate.instant(
              'features.integrations.credentials.success.title',
            ),
            appearance: 'success',
          },
        )
        .subscribe();
      this.context.completeWith(true);
    };

    this.submitCredentialsFn(this.provider, payload).subscribe({
      next: () => handleSuccess(),
      complete: () => handleSuccess(),
      error: (error: Error) => {
        console.error('Error creating API key:', error);
        this.alerts
          .open(
            this.translate.instant(
              'features.integrations.credentials.error.message',
            ),
            {
              label: this.translate.instant(
                'features.integrations.credentials.error.title',
              ),
              appearance: 'error',
            },
          )
          .subscribe();
        this.submitting = false;
      },
    });
  }

  close() {
    this.context.completeWith(false);
  }
}
